import { createRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Smartphone } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopSecurity } from "./desktop/security.tsx";
import { SubPage } from "../components/layout.tsx";
import { Button, Empty, ErrorBlock, Spinner } from "../components/ui.tsx";
import { api } from "../api/client.ts";
import { ChangePassword } from "../components/change-password.tsx";
import { useMe } from "../api/queries.ts";

/**
 * 账号安全 —— 登录设备。
 *
 * 🔴 这个平台上的账号是有钱的（分销商账上有余额、能提现），
 *    "我的账号在哪些设备登过、能不能踢掉"是刚需而不是锦上添花。
 *    接口早就有了，但一直没有任何地方能看到。
 */

interface SessionRow {
  id: number;
  userAgent: string;
  createdAt: string;
  expiresAt: string;
  /** 服务端标出来的，前端拿不到 jti。 */
  isCurrent: boolean;
}

/**
 * 从 UA 里认出设备。
 * ⚠️ 只做粗略识别 —— UA 本来就不可靠，认不出就说"其他设备"，别硬猜出个具体型号。
 */
function deviceOf(ua: string): string {
  if (!ua) return "未知设备";
  if (/iPhone|iPad/i.test(ua)) return "iPhone / iPad";
  if (/Android/i.test(ua)) return "Android 手机";
  if (/Macintosh/i.test(ua)) return "Mac";
  if (/Windows/i.test(ua)) return "Windows 电脑";
  if (/MicroMessenger/i.test(ua)) return "微信内置浏览器";
  return "其他设备";
}

function Security() {
  const qc = useQueryClient();
  const { data: me, isPending: mePending } = useMe();

  const sessions = useQuery({
    queryKey: ["me", "sessions"],
    queryFn: () => api.get<SessionRow[]>("/auth/me/sessions"),
    enabled: Boolean(me),
  });

  const revokeOthers = useMutation({
    mutationFn: () => api.post<{ revoked: number }>("/auth/me/sessions/revoke-others", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "sessions"] }),
  });

  if (mePending) {
    return (
      <SubPage title="账号安全">
        <Spinner />
      </SubPage>
    );
  }
  if (!me) {
    return (
      <SubPage title="账号安全">
        <Empty title="请先登录" />
      </SubPage>
    );
  }

  const others = (sessions.data ?? []).filter((s) => !s.isCurrent).length;

  return (
    <SubPage title="账号安全">
      {/*
        🔴 **修改密码以前哪儿都没有。** 接口（`POST /auth/me/password`）
           早就写好了，前端一处都没引用 —— 这一页只有登录设备一块，
           等于这个站的用户永远改不了自己的密码。
        ⚠️ 放在设备列表**前面**：改密码是主动动作（我要改），
           看设备是排查动作（我怀疑有人登过）。前者更常用。
      */}
      <section className="bg-white px-4 py-4">
        <h2 className="mb-3 text-sm font-medium">修改密码</h2>
        <ChangePassword />
      </section>

      <div className="mt-2 bg-brand-50 px-4 py-2.5 text-xs text-brand-700">
        这里是你的账号登录过的设备。发现不认识的，点下面的按钮把它们全踢掉。
      </div>

      {sessions.isPending ? (
        <Spinner />
      ) : sessions.error ? (
        <ErrorBlock error={sessions.error} onRetry={() => sessions.refetch()} />
      ) : (
        <div className="mt-2 bg-white">
          {sessions.data!.map((s) => (
            <div
              key={s.id}
              className="flex items-center gap-3 border-b border-neutral-100 px-4 py-3 last:border-0"
            >
              <Smartphone className="size-4 shrink-0 text-ink-500" />
              <div className="min-w-0 flex-1">
                <p className="text-sm">
                  {deviceOf(s.userAgent)}
                  {s.isCurrent && (
                    <span className="ml-1.5 rounded bg-brand-50 px-1.5 py-0.5 text-[11px] text-brand-700">
                      当前设备
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-ink-500">
                  {new Date(s.createdAt).toLocaleString("zh-CN")} 登录
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="px-4 py-4">
        {revokeOthers.isSuccess ? (
          <p className="text-center text-sm text-emerald-700">
            已退出 {revokeOthers.data.revoked} 个设备
          </p>
        ) : (
          <Button
            variant="outline"
            className="w-full"
            loading={revokeOthers.isPending}
            disabled={others === 0}
            onClick={() => revokeOthers.mutate()}
          >
            {others === 0 ? "只有当前设备登录着" : `退出其他 ${others} 个设备`}
          </Button>
        )}
        {/* ⚠️ 说清楚不会把他自己踢下线，否则他不敢点 */}
        <p className="mt-2 text-center text-xs text-ink-500">
          不会退出当前设备，你不用重新登录。
        </p>
        {revokeOthers.error && <ErrorBlock error={revokeOthers.error} />}
      </div>
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function SecurityRoute() {
  return useIsDesktop() ? <DesktopSecurity /> : <Security />;
}

export const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/security",
  component: SecurityRoute,
});
